// Catálogo de acciones MDM: qué botones pinta la ficha, en qué grupo y con qué payload.
// Si la acción APLICA a un equipo lo decide `actionEligibility.ts`, no este archivo.
import { MdmOperationTypeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationTypeEnum';
import { formatTimeMx } from '@/lib/dateHelpers';

export type MdmActionKey =
    | 'lock'
    | 'temporaryLock'
    | 'unlock'
    | 'refresh'
    | 'notify'
    | 'extendValidity'
    | 'pinUnlock'
    | 'release';

/** Cómo se comporta la acción: manda el estilo del botón y el modo del diálogo. */
export type MdmActionKind = 'operational' | 'query' | 'async' | 'sensitive' | 'destructive';

/** Campos que el diálogo le pide al operador antes de ejecutar. */
export type MdmActionPayloadField =
    | 'newDueDate'
    | 'devicePasskey'
    /** El aviso del catálogo y su intensidad; el texto lo pone el conector. */
    | 'notification'
    /** El motivo de la liberación, su nota y el IMEI tecleado que confirma lo irreversible. */
    | 'release';

/**
 * Los grupos describen la SITUACIÓN DEL CLIENTE, no lo que le pasa al equipo. Quien abre esta
 * pantalla es el manager del crédito y llega con «este cliente me está llamando», no con «este
 * equipo necesita un AUTO-LOCK-SCHEDULE».
 */
export type MdmActionGroupKey =
    | 'customerPaid'
    | 'customerNotPaying'
    | 'customerCantUse'
    | 'creditEnded';

export interface MdmAction {
    key: MdmActionKey;
    /** Etiqueta visible del botón. */
    label: string;
    description: string;
    kind: MdmActionKind;
    /** El grupo de la ficha. Ausente cuando la acción no describe una situación del cliente. */
    group?: MdmActionGroupKey;
    payloadFields: readonly MdmActionPayloadField[];
    /**
     * Operaciones del connector que la acción puede usar; algunas solo en un proveedor. Es
     * documentación del catálogo: la elegibilidad NO se deriva de aquí.
     */
    providerOperations: readonly MdmOperationTypeEnum[];
    /** `false` = no existe endpoint publicado; la UI lo dice en vez de simular éxito. */
    hasBackendContract: boolean;
    /** Por qué no hay backend, para que el diálogo lo muestre tal cual. */
    unavailableNote?: string;
}

export interface MdmActionGroup {
    key: MdmActionGroupKey;
    title: string;
    actions: readonly MdmAction[];
}

export const MDM_ACTION_GROUP_TITLE: Record<MdmActionGroupKey, string> = {
    customerPaid: 'El cliente pagó',
    customerNotPaying: 'El cliente no paga',
    customerCantUse: 'El cliente no puede usar su equipo',
    creditEnded: 'El crédito terminó',
};

/**
 * El catálogo es UNO solo. Lo que separaba a los dos proveedores del enum —DEACTIVATE y ARCHIVE—
 * salió por decisión de diseño; el diagnóstico se ofrece completo porque el operador tiene que
 * ver qué operaciones existen, aunque hoy ninguna esté construida.
 */
const MDM_ACTIONS: readonly MdmAction[] = [
    {
        key: 'unlock',
        label: 'Desbloquear',
        description:
            'Levanta el bloqueo del equipo. Necesita que el equipo tenga señal. NO lo saca del MDM: sigue administrado.',
        kind: 'operational',
        group: 'customerPaid',
        payloadFields: [],
        providerOperations: [MdmOperationTypeEnum.UNLOCK],
        hasBackendContract: true,
    },
    {
        key: 'extendValidity',
        label: 'Extender vigencia',
        description:
            'Desbloquea el equipo y reprograma el corte a la fecha que pactes. Mueve una fecha de cobro.',
        kind: 'sensitive',
        group: 'customerPaid',
        payloadFields: ['newDueDate'],
        providerOperations: [MdmOperationTypeEnum.UNLOCK, MdmOperationTypeEnum.AUTO_LOCK_SCHEDULE],
        hasBackendContract: true,
    },
    {
        key: 'lock',
        label: 'Bloquear',
        description:
            'Programa el candado del equipo: la hora del corte la informa el proveedor. Necesita que el equipo tenga señal.',
        kind: 'operational',
        group: 'customerNotPaying',
        payloadFields: [],
        providerOperations: [MdmOperationTypeEnum.LOCK],
        hasBackendContract: true,
    },
    {
        key: 'temporaryLock',
        label: 'Bloqueo temporal',
        description:
            'Bloquea el equipo por un rato y lo suelta solo al vencer el plazo, sin una segunda orden.',
        kind: 'operational',
        group: 'customerNotPaying',
        payloadFields: [],
        providerOperations: [MdmOperationTypeEnum.LOCK],
        hasBackendContract: false,
        unavailableNote:
            'Ningún proveedor de la flota admite el bloqueo temporal en estos equipos, así que no hay endpoint que lo ejecute.',
    },
    {
        key: 'notify',
        label: 'Notificar',
        description:
            'Muestra un aviso en la pantalla del equipo. No lo bloquea ni lo desbloquea: el estado del expediente no cambia.',
        kind: 'operational',
        group: 'customerNotPaying',
        payloadFields: ['notification'],
        providerOperations: [MdmOperationTypeEnum.NOTIFY],
        hasBackendContract: true,
    },
    {
        key: 'pinUnlock',
        label: 'PIN de desbloqueo',
        description:
            'Abre el equipo un rato. NO levanta el candado: el bloqueo sigue vigente del lado del proveedor y el equipo se vuelve a bloquear solo. El expediente no cambia.',
        kind: 'sensitive',
        group: 'customerCantUse',
        payloadFields: ['devicePasskey'],
        providerOperations: [MdmOperationTypeEnum.PIN_UNLOCK],
        hasBackendContract: true,
    },
    {
        key: 'refresh',
        label: 'Actualizar desde el proveedor',
        description:
            'Le pregunta al proveedor cómo ve el equipo ahora mismo y asienta lo que conteste.',
        kind: 'query',
        payloadFields: [],
        providerOperations: [MdmOperationTypeEnum.STATUS, MdmOperationTypeEnum.LAST_SEEN],
        hasBackendContract: true,
    },
    {
        key: 'release',
        label: 'Liberar del MDM',
        description:
            'Saca el equipo del MDM para siempre: deja de estar administrado y nunca más se podrá bloquear ni desbloquear. No es «Desbloquear».',
        kind: 'destructive',
        group: 'creditEnded',
        payloadFields: ['release'],
        providerOperations: [MdmOperationTypeEnum.RELEASE],
        hasBackendContract: true,
    },
];

/** El catálogo completo, en el orden de la ficha: por situación del cliente, no alfabético. */
export function getMdmActions(): readonly MdmAction[] {
    return MDM_ACTIONS;
}

/** El catálogo partido en los grupos que pinta la ficha. */
export function getMdmActionGroups(): readonly MdmActionGroup[] {
    // Se agrupa por clave, no por adyacencia: una acción fuera de orden partiría su grupo en dos
    // cards con la misma `key`, y React se queda con la última.
    const byKey = new Map<MdmActionGroupKey, MdmAction[]>();
    for (const action of MDM_ACTIONS) {
        if (!action.group) continue;
        const bucket = byKey.get(action.group);
        if (bucket) bucket.push(action);
        else byKey.set(action.group, [action]);
    }
    return [...byKey].map(([key, actions]) => ({
        key,
        title: MDM_ACTION_GROUP_TITLE[key],
        actions,
    }));
}

/** La acción del catálogo con esa clave. Siempre existe: el catálogo es uno solo. */
export function findAction(key: MdmActionKey): MdmAction | undefined {
    return MDM_ACTIONS.find((action) => action.key === key);
}

/** Las acciones que dejan línea en la bitácora del equipo: hoy, todas las que tienen endpoint. */
const ACTIONS_LOGGED_IN_AUDIT: readonly MdmActionKey[] = [
    'lock',
    'unlock',
    'refresh',
    'extendValidity',
    'pinUnlock',
    'notify',
    'release',
];

/** Qué deja la acción en la bitácora del equipo: lo dice el toast que la confirma. */
/**
 * El peor desenlace de una orden: el desbloqueo salió y el equipo quedó sin bloqueo automático. El
 * lambda NO vuelve a bloquearlo, así que si nadie lo escala se queda suelto.
 */
export function unprotectedDevicesTitle(total: number): string {
    return total === 1
        ? 'El equipo quedó sin bloqueo automático'
        : `${String(total)} equipos quedaron sin bloqueo automático`;
}

export const UNPROTECTED_DEVICES_DETAIL =
    'El proveedor recibió el desbloqueo, pero la reprogramación del corte no se instaló. Escálalo: nadie lo va a volver a bloquear solo.';

export function describeActionOutcome(key: MdmActionKey): string {
    if (!ACTIONS_LOGGED_IN_AUDIT.includes(key)) return 'esta acción no deja línea en la bitácora.';
    return 'el resultado queda en la bitácora del equipo.';
}

const UNKNOWN_CUT_TIME = 'el proveedor no informó a qué hora se aplica el corte';

/** A qué hora cae el corte del bloqueo que se acaba de mandar; sin hora informada, lo dice. */
export function describeLockEffective(cutAt: string | undefined): string {
    return cutAt ? `el corte se aplica a las ${formatTimeMx(cutAt)}` : UNKNOWN_CUT_TIME;
}

/** Lo que dice el toast que confirma la acción: a qué hora cae el corte y qué deja registrado. */
export function describeActionSent(key: MdmActionKey, lockEffectiveAt?: string): string {
    const outcome = describeActionOutcome(key);
    if (key !== 'lock') return outcome;
    if (!lockEffectiveAt) {
        return `bloqueo enviado; ${UNKNOWN_CUT_TIME}. ${outcome.charAt(0).toUpperCase()}${outcome.slice(1)}`;
    }
    return `bloqueo programado para las ${formatTimeMx(lockEffectiveAt)}; ${outcome}`;
}
